/**
 * AccountMenu — small dropdown in the header showing the current user's info
 * with two actions: "Change password" and "Logout".
 * Any authenticated user (regardless of role) can use it to rotate their own
 * password from inside the dashboard — no code change or admin help needed.
 */
import { useEffect, useRef, useState } from 'react';
import { opsApi, useOpsAuth, ROLE_LABELS_AR, formatAuthError } from './OpsAuthContext';

const AccountMenu = () => {
  const { user, logout } = useOpsAuth();
  const [open, setOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid="account-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full hover:bg-[#F3F1E9] px-2 py-1 transition"
      >
        <div className="w-9 h-9 rounded-full bg-[#1F3D2B] text-white flex items-center justify-center font-['Noto_Kufi_Arabic'] font-bold text-[13px]">
          {(user.name_ar || user.name_en || '?').trim().charAt(0)}
        </div>
        <div className="text-end hidden sm:block">
          <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-semibold text-[#1F3D2B]">
            {user.name_ar}
          </p>
          <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#7A866E]">
            {ROLE_LABELS_AR[user.role]}
          </p>
        </div>
      </button>

      {open && (
        <div
          data-testid="account-menu-dropdown"
          className="absolute end-0 mt-2 w-[260px] bg-white rounded-2xl ring-1 ring-[#E6E0D6] shadow-2xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-[#EFEAE1]">
            <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-semibold text-[#1F3D2B]">
              {user.name_ar}
            </p>
            <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#7A866E] mt-0.5" dir="ltr">
              {user.email}
            </p>
            <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#4A5D23] mt-1">
              {ROLE_LABELS_AR[user.role]}
            </p>
          </div>
          <button
            data-testid="account-change-password"
            onClick={() => { setShowPwd(true); setOpen(false); }}
            className="w-full text-start px-4 py-3 font-['Noto_Kufi_Arabic'] text-[13px] text-[#1F3D2B] hover:bg-[#FAFAF7]"
          >
            تغيير كلمة المرور
          </button>
          <button
            data-testid="account-logout"
            onClick={logout}
            className="w-full text-start px-4 py-3 font-['Noto_Kufi_Arabic'] text-[13px] text-[#B84A3A] hover:bg-[#FBEDEA] border-t border-[#EFEAE1]"
          >
            تسجيل الخروج
          </button>
        </div>
      )}

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
};

// ---- Change Password Modal ----
const ChangePasswordModal = ({ onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (next.length < 6) {
      setErr('يجب أن تكون كلمة المرور الجديدة ٦ أحرف على الأقل');
      return;
    }
    if (next !== confirm) {
      setErr('تأكيد كلمة المرور لا يطابق');
      return;
    }
    setBusy(true);
    try {
      await opsApi.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      setOk(true);
      setTimeout(onClose, 1500);
    } catch (e2) {
      setErr(formatAuthError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" dir="rtl">
      <form
        data-testid="change-password-modal"
        onSubmit={submit}
        className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl"
      >
        <h3 className="font-['Noto_Kufi_Arabic'] text-[18px] font-bold text-[#1F3D2B]">
          تغيير كلمة المرور
        </h3>
        <p className="text-[12px] font-['Noto_Kufi_Arabic'] text-[#7A866E] mt-1">
          للحماية، أدخلي كلمة المرور الحالية أولًا.
        </p>
        <div className="space-y-3 mt-4">
          <input
            data-testid="cp-current"
            type="password"
            required
            autoFocus
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="كلمة المرور الحالية"
            className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2"
          />
          <input
            data-testid="cp-new"
            type="password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="كلمة المرور الجديدة (٦ أحرف على الأقل)"
            className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2"
          />
          <input
            data-testid="cp-confirm"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="تأكيد كلمة المرور الجديدة"
            className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2"
          />
        </div>
        {err && <p className="text-[12px] text-[#B84A3A] font-['Noto_Kufi_Arabic'] mt-3">{err}</p>}
        {ok && (
          <p className="text-[12px] text-[#3F5021] font-['Noto_Kufi_Arabic'] mt-3">
            تم تحديث كلمة المرور ✅
          </p>
        )}
        <div className="flex gap-2 mt-5">
          <button
            type="submit"
            data-testid="cp-submit"
            disabled={busy || ok}
            className="text-[13px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-5 py-2 rounded-lg hover:bg-[#163020] disabled:opacity-50"
          >
            {busy ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] font-['Noto_Kufi_Arabic'] text-[#3D512D] px-5 py-2 rounded-lg hover:bg-[#F3F1E9]"
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountMenu;
